'use client'

import { Calendar, Clock, ShoppingCart } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import { IGiftDeal, IGiftProduct, IGlobalDiscount } from '../types/schema'

interface ProductCardProps {
  product: IGiftProduct
  promotion?: IGiftDeal
  onBuyNow: (product: IGiftProduct) => void
  locale: string
  globalDiscount?: IGlobalDiscount
}

const decodeHtmlEntities = (str: string) => {
  if (!str) return ''
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
}

export default function ProductCard({
  product,
  promotion,
  onBuyNow,
  locale,
  globalDiscount
}: ProductCardProps) {
  const t = useTranslations()

  // Format duration display
  const formatDuration = (hours: number) => {
    if (hours >= 1) {
      const wholeHours = Math.floor(hours)
      const minutes = Math.round((hours - wholeHours) * 60)
      if (minutes > 0) {
        return `${wholeHours} ${wholeHours > 1 ? t('eGift : Home Page.hours_unit') : t('eGift : Home Page.hour_unit')} ${minutes} ${t('eGift : Home Page.minutes_unit')}`
      }
      return `${wholeHours} ${wholeHours > 1 ? t('eGift : Home Page.hours_unit') : t('eGift : Home Page.hour_unit')}`
    } else {
      const minutes = Math.round(hours * 60)
      return `${minutes} ${t('eGift : Home Page.minutes_unit')}`
    }
  }

  // Get expiry with priority and fallback logic
  const getExpiryDays = () => {
    if (product.expiry && product.expiry > 0) {
      return product.expiry
    }

    if (globalDiscount?.expiry && globalDiscount.expiry > 0) {
      return globalDiscount.expiry
    }

    return 365
  }

  // Format expiry display
  const formatExpiry = (days: number) => {
    if (days >= 365) {
      const years = Math.floor(days / 365)
      const remainingDays = days % 365
      let result = `${years} ${years > 1 ? t('eGift : Home Page.years_unit') : t('eGift : Home Page.year_unit')}`
      if (remainingDays > 0) {
        if (remainingDays >= 30) {
          const months = Math.floor(remainingDays / 30)
          result += ` ${months} ${months > 1 ? t('eGift : Home Page.months_unit') : t('eGift : Home Page.month_unit')}`
        } else {
          result += ` ${remainingDays} ${remainingDays > 1 ? t('eGift : Home Page.days_unit') : t('eGift : Home Page.day_unit')}`
        }
      }
      return result
    } else if (days >= 30) {
      const months = Math.floor(days / 30)
      const remainingDays = days % 30
      let result = `${months} ${months > 1 ? t('eGift : Home Page.months_unit') : t('eGift : Home Page.month_unit')}`
      if (remainingDays > 0) {
        result += ` ${remainingDays} ${remainingDays > 1 ? t('eGift : Home Page.days_unit') : t('eGift : Home Page.day_unit')}`
      }
      return result
    } else {
      return `${days} ${days > 1 ? t('eGift : Home Page.days_unit') : t('eGift : Home Page.day_unit')}`
    }
  }


  return (
    <div className='group relative flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl'>
      <div className='relative overflow-hidden'>
        <Image
          src={product.imgURI}
          alt={decodeHtmlEntities(product.name)}
          width={400}
          height={300}
          className='h-48 w-full object-cover transition-transform duration-500 group-hover:scale-110'
        />

        {/* Discount Badge */}
        {(promotion?.discountPercent || promotion?.savings) && (
          <div className='absolute left-0 top-4 z-10'>
            <div className='relative bg-gradient-to-r from-amber-500 to-amber-600 px-3 py-1 pr-4 text-xs font-bold text-white shadow-md after:absolute after:right-0 after:top-0 after:h-full after:w-4 after:translate-x-full after:bg-gradient-to-r after:from-amber-600 after:to-transparent after:content-[""]'>
              {t('eGift : Purchase.ONLINEGIFT_saved')}{' '}
              {promotion.discountPercent
                ? `${promotion.discountPercent}%`
                : `${Number(promotion.savings).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  })} THB`}
            </div>
          </div>
        )}
      </div>

      <div className='flex flex-1 flex-col p-5'>
        <h4 className='mb-2 line-clamp-2 h-auto text-lg font-bold capitalize leading-tight text-gray-800 sm:h-[3.5rem]'>
          {decodeHtmlEntities(product.name)}
        </h4>

        <div className='mb-4 flex items-center gap-4 text-xs text-gray-500'>
          <div className='flex items-center gap-1'>
            <Clock className='h-3.5 w-3.5 text-amber-500' />
            <span>
              {t('eGift : Home Page.duration')}: {formatDuration(product.hour)}
            </span>
          </div>
          <div className='flex items-center gap-1'>
            <Calendar className='h-3.5 w-3.5 text-amber-500' />
            <span>
              {t('eGift : Home Page.expires')}: {formatExpiry(getExpiryDays())}
            </span>
          </div>
        </div>

        <div className='mt-auto flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
          {/* Price Display */}
          <div className='flex flex-col'>
            {promotion?.salePrice ? (
              <>
                <span className='text-xs text-gray-400 line-through'>
                  ฿
                  {Number(product.price).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  })}
                </span>
                <span className='text-xl font-bold text-amber-600'>
                  ฿
                  {promotion.salePrice.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  })}
                  <span className='ml-1 text-xs font-normal text-gray-500'>THB</span>
                </span>
              </>
            ) : (
              <span className='text-xl font-bold text-amber-600'>
                ฿
                {Number(product.price).toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2
                })}
                <span className='ml-1 text-xs font-normal text-gray-500'>THB</span>
              </span>
            )}
          </div>
          <button
            data-track="add-to-cart"
            data-name={product?.name || 'Unknown Product'}
            onClick={() => onBuyNow(product)}
            className='w-full rounded-full bg-gray-900 px-4 py-2 text-center text-xs font-bold uppercase text-white transition-colors hover:bg-orange-500 sm:w-auto'>
            <span className='mr-1 inline-block align-text-bottom'>
              <ShoppingCart className='h-4 w-4' />
            </span>{' '}
            {t('eGift : Home Page.GIFTCERTIFICATE_buy_now').toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  )
}
